import { urlFromStoryblokLinkField } from '~/utils/helpers';
import normalizeUrl from '~/utils/normalizeUrl';

export interface CanonicalUrlOptions {
    baseUrl: string;
    storySlug: string;
    canonicalStoryblokLink?: {
        id?: string;
        linktype?: string;
        fieldtype?: string;
        cached_url?: string;
        story?: {
            url?: string;
            full_slug?: string;
        };
        url?: string;
    };
    pageQuery?: string | number;
}

export const buildCanonicalUrl = (options: CanonicalUrlOptions): string => {
    const { baseUrl, storySlug, canonicalStoryblokLink, pageQuery } = options;

    let canonicalUrl = '';

    if (canonicalStoryblokLink) {
        canonicalUrl = urlFromStoryblokLinkField(canonicalStoryblokLink);
    }

    if (!canonicalUrl) {
        canonicalUrl = normalizeUrl(storySlug);
    }

    if (pageQuery) {
        canonicalUrl = addPageParameterToUrl(canonicalUrl, pageQuery);
    }

    if (!isAbsoluteUrl(canonicalUrl)) {
        canonicalUrl = `${baseUrl}${canonicalUrl}`;
    }

    return normalizeUrl(canonicalUrl);
};

const addPageParameterToUrl = (
    url: string,
    pageValue: string | number,
): string => {
    // For relative URLs, use a temporary base to enable URL parsing
    // For protocol-relative URLs (//example.com), add https: scheme for parsing
    const tempBase = isAbsoluteUrl(url) ? undefined : 'http://temp.local';
    const parseableUrl = url.startsWith('//') ? `https:${url}` : url;
    const urlObj = new URL(parseableUrl, tempBase);

    urlObj.searchParams.set('page', String(pageValue));

    if (tempBase) {
        // Relative URL: extract pathname + search + hash
        return urlObj.pathname + urlObj.search + urlObj.hash;
    }

    if (url.startsWith('//')) {
        // Protocol-relative URL: restore the original format
        return urlObj.href.replace(/^https:/, '');
    }

    return urlObj.href;
};

const isAbsoluteUrl = (url: string): boolean => {
    if (url.startsWith('//')) {
        return true; // Protocol-relative URLs (//example.com) are absolute
    }

    if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {
        return true; // URLs with schemes (http:, https:, mailto:, tel:, etc.) are absolute
    }

    return false;
};

export default buildCanonicalUrl;
